"use client";

import type { MotionValue } from "motion/react";
import { type TransitionEvent, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { CardRecto, CardVerso } from "../business-card";

import {
  CAMERA,
  CARD_RATIO,
  createFallTrack,
  createPose,
  getStageLayout,
  sampleCardPose,
  TIMELINE,
} from "./choreography";

// =============================================================================

type CardFallbackProps = {
  progress: MotionValue<number>;
  role: string;
  tags: string[];
  qrSvg: string;
  className?: string;
  onTransitionEnd?: (event: TransitionEvent<HTMLDivElement>) => void;
};

type Size = { width: number; height: number };

const DEG = 180 / Math.PI;

/**
 * HTML version of the card (no WebGL, or while the 3D chunk loads): same
 * choreography as the WebGL scene, seen through a CSS perspective matching
 * its camera. Transforms are written directly on scroll, without re-renders.
 */
export function CardFallback({
  progress,
  role,
  tags,
  qrSvg,
  className,
  onTransitionEnd,
}: CardFallbackProps) {
  const stage = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const shadow = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size | null>(null);

  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize((previous) =>
        previous?.width === width && previous.height === height
          ? previous
          : { width, height }
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!size) return;
    const layout = getStageLayout(size.width, size.height);
    const fall = createFallTrack(layout);
    const pose = createPose();
    const landing = TIMELINE.landing;

    const update = (value: number) => {
      sampleCardPose(value, fall, pose);
      const x = (pose.x * size.width) / 2;
      const y = (-pose.y * size.height) / 2;
      // Y points down in CSS: rotations around X and Z change sign
      if (card.current) {
        card.current.style.transform = `translate3d(${x}px, ${y}px, 0) rotateX(${-CAMERA.elevation}deg) rotateY(${pose.yaw * DEG}deg) rotateX(${-pose.tilt * DEG}deg) rotateZ(${-pose.roll * DEG}deg) rotateY(${pose.flip * DEG}deg)`;
      }
      if (shadow.current) {
        const near = Math.min(
          1,
          Math.max(0, (value - (landing - 0.16)) / 0.16)
        );
        shadow.current.style.opacity = String(near * near);
        shadow.current.style.transform = `translate3d(0, ${(-layout.landY * size.height) / 2}px, 0) scale(${0.7 + near * 0.3})`;
      }
    };

    update(progress.get());
    return progress.on("change", update);
  }, [progress, size]);

  const layout = size ? getStageLayout(size.width, size.height) : null;
  const perspective = size
    ? size.height / 2 / Math.tan((CAMERA.fov * Math.PI) / 360)
    : 1000;

  return (
    <div
      ref={stage}
      className={cn("absolute inset-0", className)}
      style={{ perspective: `${perspective}px` }}
      onTransitionEnd={onTransitionEnd}
    >
      {layout && (
        <>
          {/* Contact shadow on the floor */}
          <div
            ref={shadow}
            aria-hidden
            className="absolute top-1/2 left-1/2 rounded-[50%] bg-black/45 opacity-0 blur-2xl dark:bg-black/70"
            style={{
              width: layout.cardWidth * 0.95,
              height: layout.cardWidth * CARD_RATIO * 0.5,
              marginLeft: (-layout.cardWidth * 0.95) / 2,
              marginTop: (-layout.cardWidth * CARD_RATIO * 0.5) / 2,
            }}
          />
          <div
            ref={card}
            className="@container absolute top-1/2 left-1/2 [transform-style:preserve-3d]"
            style={{
              width: layout.cardWidth,
              height: layout.cardWidth * CARD_RATIO,
              marginLeft: -layout.cardWidth / 2,
              marginTop: (-layout.cardWidth * CARD_RATIO) / 2,
            }}
          >
            <CardRecto role={role} />
            <CardVerso tags={tags} qrSvg={qrSvg} />
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
